import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
  onSnapshot,
  arrayUnion,
} from 'firebase/firestore';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { httpsCallable } from 'firebase/functions';

// Import types from the central enhanced_organization.ts file
import {
  EnhancedOrgType,
  EnhancedOrganisation as EnhancedOrganization,
  OrganisationSettings as OrganizationSettings,
  NotificationSettings,
  OrganisationFeature,
  WorkingHours,
  DaySchedule,
  BookingRules,
  CustomField,
  OrganisationStaffRole as StaffRole,
  OrganisationStaffMember as StaffMember,
  OrganisationPreset as OrganizationPreset,
  Booking,
  BookingStatus,
  PaymentStatus,
  CreateBookingRequest,
  UpdateBookingRequest,
  BookingResponse,
  BookingsResponse,
  BookingFilters,
  Room,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomResponse,
  RoomsResponse,
  AvailabilitySettings,
  RoomAvailability,
  CreateAvailabilitySettingsRequest,
  UpdateAvailabilitySettingsRequest,
  AvailabilitySettingsResponse,
  CreateRoomAvailabilityRequest,
  UpdateRoomAvailabilityRequest,
  RoomAvailabilityResponse,
  GetAvailableTimeSlotsRequest,
  GetAvailableTimeSlotsResponse,
  Equipment,
  EquipmentType,
  EquipmentCondition,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
  EquipmentResponse,
  EquipmentListResponse,
  EquipmentFilters,
  Engineer,
  EngineerAvailability,
  CreateEngineerRequest,
  UpdateEngineerRequest,
  EngineerResponse,
  EngineersResponse,
  EngineerFilters,
  CreateEngineerAvailabilityRequest,
  UpdateEngineerAvailabilityRequest,
  EngineerAvailabilityResponse,
  ENGINEER_SPECIALTIES,
  MusicShowcase,
  MusicTrack,
  MusicPlatform,
  CreateMusicShowcaseRequest,
  UpdateMusicShowcaseRequest,
  CreateMusicTrackRequest,
  UpdateMusicTrackRequest,
  MusicShowcaseResponse,
  MusicTrackResponse,
  MusicTracksResponse,
  MusicTrackFilters,
  generateEmbedCode,
  validateMusicUrl,
  getDefaultBookingCustomerFields,
} from 'src/@types/enhanced_organization';

// Re-export types for external use
export type {
  EnhancedOrgType,
  EnhancedOrganization,
  OrganizationSettings,
  NotificationSettings,
  OrganisationFeature,
  WorkingHours,
  DaySchedule,
  BookingRules,
  CustomField,
  StaffRole,
  StaffMember,
  OrganizationPreset,
  Room,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomResponse,
  RoomsResponse,
  Equipment,
  EquipmentType,
  EquipmentCondition,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
  EquipmentResponse,
  EquipmentListResponse,
  EquipmentFilters,
  MusicShowcase,
  MusicTrack,
  MusicPlatform,
  CreateMusicShowcaseRequest,
  UpdateMusicShowcaseRequest,
  CreateMusicTrackRequest,
  UpdateMusicTrackRequest,
  MusicShowcaseResponse,
  MusicTrackResponse,
  MusicTracksResponse,
  MusicTrackFilters,
};

// Onboarding flow interfaces
export interface OrganizationOnboardingStep1 {
  name: string;
  description: string;
  language: 'en' | 'fr';
  logo?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

export interface OrganizationOnboardingStep2 {
  type: EnhancedOrgType;
}

export interface OrganizationOnboardingStep3 {
  roles: string[];
}

export interface OrganizationOnboardingStep4 {
  staffMembers: Omit<StaffMember, 'id' | 'organizationId' | 'ownerId' | 'createdAt'>[];
}

export interface CompleteOnboardingRequest {
  step1: OrganizationOnboardingStep1;
  step2: OrganizationOnboardingStep2;
  step3: OrganizationOnboardingStep3;
  step4: OrganizationOnboardingStep4;
  ownerId: string;
}

// API Response types
export interface FetchEnhancedOrganizationsResponse {
  organizations: EnhancedOrganization[];
}

export interface FetchEnhancedOrganizationResponse {
  organization: EnhancedOrganization;
}

export interface CreateOrganizationResponse {
  organization: EnhancedOrganization;
  success: boolean;
  message?: string;
}

// Helper function to build Firestore query from filters
const buildBookingsQuery = (filters: BookingFilters) => {
  let bookingsQuery = query(collection(DB, 'bookings'));

  if (filters.organizationId) {
    bookingsQuery = query(bookingsQuery, where('organizationId', '==', filters.organizationId));
  }

  if (filters.roomId) {
    bookingsQuery = query(bookingsQuery, where('listingId', '==', filters.roomId));
  }

  if (filters.customerEmail) {
    bookingsQuery = query(bookingsQuery, where('customerEmail', '==', filters.customerEmail));
  }

  if (filters.status) {
    if (Array.isArray(filters.status)) {
      bookingsQuery = query(bookingsQuery, where('status', 'in', filters.status));
    } else {
      bookingsQuery = query(bookingsQuery, where('status', '==', filters.status));
    }
  }

  if (filters.paymentStatus) {
    bookingsQuery = query(bookingsQuery, where('paymentStatus', '==', filters.paymentStatus));
  }

  // Date range filtering
  if (filters.startDate) {
    const startDate = Timestamp.fromDate(new Date(filters.startDate));
    bookingsQuery = query(bookingsQuery, where('startDateTime', '>=', startDate));
  }

  if (filters.endDate) {
    const endDate = Timestamp.fromDate(new Date(filters.endDate));
    bookingsQuery = query(bookingsQuery, where('startDateTime', '<=', endDate));
  }

  return bookingsQuery;
};

// Enhanced Organizations API
export const EnhancedOrganizationsApi = createApi({
  reducerPath: 'enhancedOrganizationsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'EnhancedOrganization',
    'EnhancedOrganizations',
    'StaffRole',
    'StaffMember',
    'Room',
    'Rooms',
    'AvailabilitySettings',
    'RoomAvailability',
    'Booking',
    'Bookings',
    'AvailableTimeSlots',
    'Equipment',
    'EquipmentList',
    'MusicShowcase',
    'MusicTrack',
    'MusicTracks',
  ],
  endpoints: (builder) => ({
    // Get all enhanced organizations for a user
    getEnhancedOrganizations: builder.query<
      FetchEnhancedOrganizationsResponse,
      { userId: string; userRole?: string }
    >({
      async queryFn({ userId, userRole }) {
        try {
          const organizationsRef = collection(DB, 'enhanced_organizations');
          let organizationsQuery = query(organizationsRef, where('ownerId', '==', userId));

          // If not owner, check if user is staff member
          if (userRole !== 'Owner') {
            // Could extend to check staff membership
            organizationsQuery = query(organizationsRef, where('isDeleted', '!=', true));
          }

          const querySnapshot = await getDocs(organizationsQuery);

          const organizations: EnhancedOrganization[] = [];
          querySnapshot.forEach((doc) => {
            organizations.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
              updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
            } as EnhancedOrganization);
          });

          return { data: { organizations } };
        } catch (error: any) {
          console.error('Error fetching enhanced organizations:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organizations',
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.organizations.map(({ id }) => ({
                type: 'EnhancedOrganization' as const,
                id,
              })),
              { type: 'EnhancedOrganizations', id: 'LIST' },
            ]
          : [{ type: 'EnhancedOrganizations', id: 'LIST' }],
    }),

    // Get organizations by type
    getOrganizationsByType: builder.query<
      FetchEnhancedOrganizationsResponse,
      { type: EnhancedOrgType; userId: string }
    >({
      async queryFn({ type, userId }) {
        try {
          const organizationsRef = collection(DB, 'enhanced_organizations');
          const organizationsQuery = query(
            organizationsRef,
            where('type', '==', type),
            where('ownerId', '==', userId),
            where('isDeleted', '!=', true)
          );

          const querySnapshot = await getDocs(organizationsQuery);

          const organizations: EnhancedOrganization[] = [];
          querySnapshot.forEach((doc) => {
            organizations.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
              updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
            } as EnhancedOrganization);
          });

          return { data: { organizations } };
        } catch (error: any) {
          console.error('Error fetching organizations by type:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organizations',
            },
          };
        }
      },
      providesTags: (result, error, { type }) => [
        { type: 'EnhancedOrganizations', id: `TYPE_${type}` },
      ],
    }),

    // Get single organization
    getEnhancedOrganization: builder.query<FetchEnhancedOrganizationResponse, string>({
      async queryFn(organizationId) {
        try {
          const organizationRef = doc(DB, 'enhanced_organizations', organizationId);
          const docSnap = await getDoc(organizationRef);

          if (docSnap.exists()) {
            const organization = {
              id: docSnap.id,
              ...docSnap.data(),
              createdAt: docSnap.data().createdAt?.toDate?.() || docSnap.data().createdAt,
              updatedAt: docSnap.data().updatedAt?.toDate?.() || docSnap.data().updatedAt,
            } as EnhancedOrganization;

            return { data: { organization } };
          } else {
            return {
              error: {
                status: 'NOT_FOUND',
                data: `Organization with ID ${organizationId} not found`,
              },
            };
          }
        } catch (error: any) {
          console.error('Error fetching organization:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organization',
            },
          };
        }
      },
      providesTags: (result, error, organizationId) => [
        { type: 'EnhancedOrganization', id: organizationId },
      ],
    }),

    // Complete organization onboarding (all steps)
    completeOrganizationOnboarding: builder.mutation<
      CreateOrganizationResponse,
      CompleteOnboardingRequest
    >({
      async queryFn(onboardingData) {
        try {
          const { step1, step2, step3, step4, ownerId } = onboardingData;

          // Create the organization document
          const organizationData: Omit<EnhancedOrganization, 'id'> = {
            name: step1.name,
            description: step1.description,
            address: step1.address,
            latitude: step1.latitude,
            longitude: step1.longitude,
            type: step2.type,
            language: step1.language,
            logo: step1.logo,
            ownerId,
            settings: getDefaultSettingsForType(step2.type),
            isDeleted: false,
            createdAt: serverTimestamp() as any,
            updatedAt: serverTimestamp() as any,
          };

          const organizationsRef = collection(DB, 'enhanced_organizations');
          const docRef = await addDoc(organizationsRef, organizationData);

          // Create staff roles if any
          if (step3.roles.length > 0) {
            const rolesPromises = step3.roles.map((roleName) =>
              addDoc(collection(DB, 'staff_roles'), {
                organizationId: docRef.id,
                name: roleName,
                createdAt: serverTimestamp() as any,
              })
            );
            await Promise.all(rolesPromises);
          }

          // Create staff members if any
          if (step4.staffMembers.length > 0) {
            const staffPromises = step4.staffMembers.map((member) =>
              addDoc(collection(DB, 'staff_members'), {
                ...member,
                organizationId: docRef.id,
                ownerId,
                isActive: true,
                createdAt: serverTimestamp() as any,
              })
            );
            await Promise.all(staffPromises);
          }

          const createdOrganization: EnhancedOrganization = {
            id: docRef.id,
            ...organizationData,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return {
            data: {
              organization: createdOrganization,
              success: true,
              message: 'Organization onboarding completed successfully',
            },
          };
        } catch (error: any) {
          console.error('Error completing organization onboarding:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred during onboarding',
            },
          };
        }
      },
      invalidatesTags: [{ type: 'EnhancedOrganizations', id: 'LIST' }],
    }),

    // Create organization (legacy compatibility)
    createEnhancedOrganization: builder.mutation<
      CreateOrganizationResponse,
      Partial<EnhancedOrganization>
    >({
      async queryFn(organizationData) {
        try {
          if (!organizationData.name || !organizationData.type || !organizationData.ownerId) {
            return {
              error: {
                status: 'INVALID_REQUEST',
                data: 'Missing required fields: name, type, or ownerId',
              },
            };
          }

          const organizationsRef = collection(DB, 'enhanced_organizations');
          const docRef = await addDoc(organizationsRef, {
            ...organizationData,
            settings: organizationData.settings || getDefaultSettingsForType(organizationData.type),
            isDeleted: false,
            createdAt: serverTimestamp() as any,
            updatedAt: serverTimestamp() as any,
          });

          const createdOrganization: EnhancedOrganization = {
            id: docRef.id,
            ...organizationData,
            createdAt: new Date(),
            updatedAt: new Date(),
          } as EnhancedOrganization;

          return {
            data: {
              organization: createdOrganization,
              success: true,
              message: 'Organization created successfully',
            },
          };
        } catch (error: any) {
          console.error('Error creating organization:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while creating organization',
            },
          };
        }
      },
      invalidatesTags: [{ type: 'EnhancedOrganizations', id: 'LIST' }],
    }),

    // Update organization
    updateEnhancedOrganization: builder.mutation<
      EnhancedOrganization,
      Partial<EnhancedOrganization> & { id: string }
    >({
      async queryFn(organizationData) {
        try {
          const { id, ...updateData } = organizationData;

          // Validate bookingCustomerFields if provided
          if (updateData.settings?.bookingCustomerFields) {
            const { validateBookingCustomerFields, mergeBookingCustomerFields } = await import(
              'src/@types/enhanced_organization'
            );

            const validationResult = validateBookingCustomerFields(
              updateData.settings.bookingCustomerFields
            );

            if (!validationResult.isValid) {
              console.error('Invalid booking customer fields:', validationResult.errors);
              return {
                error: {
                  status: 'VALIDATION_ERROR',
                  data: `Invalid booking customer fields: ${validationResult.errors.join(', ')}`,
                },
              };
            }

            // Merge with defaults to ensure default fields cannot be deleted
            updateData.settings.bookingCustomerFields = mergeBookingCustomerFields(
              updateData.settings.bookingCustomerFields
            );
          }

          const organizationRef = doc(DB, 'enhanced_organizations', id);

          await updateDoc(organizationRef, {
            ...updateData,
            updatedAt: serverTimestamp(),
          });

          return { data: organizationData as EnhancedOrganization };
        } catch (error: any) {
          console.error('Error updating organization:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while updating organization',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'EnhancedOrganization', id },
        { type: 'EnhancedOrganizations', id: 'LIST' },
      ],
    }),

    // Soft delete organization
    deleteEnhancedOrganization: builder.mutation<boolean, string>({
      async queryFn(organizationId) {
        try {
          const organizationRef = doc(DB, 'enhanced_organizations', organizationId);
          await updateDoc(organizationRef, {
            isDeleted: true,
            updatedAt: serverTimestamp(),
          });

          return { data: true };
        } catch (error: any) {
          console.error('Error deleting organization:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while deleting organization',
            },
          };
        }
      },
      invalidatesTags: (result, error, organizationId) => [
        { type: 'EnhancedOrganization', id: organizationId },
        { type: 'EnhancedOrganizations', id: 'LIST' },
      ],
    }),

    // Migrate organization to add default booking customer fields
    migrateOrganizationBookingFields: builder.mutation<
      { success: boolean; updated: number },
      { organizationId?: string }
    >({
      async queryFn({ organizationId }) {
        try {
          const { getDefaultBookingCustomerFields } = await import(
            'src/@types/enhanced_organization'
          );

          let updated = 0;

          if (organizationId) {
            // Migrate single organization
            const organizationRef = doc(DB, 'enhanced_organizations', organizationId);
            const organizationSnap = await getDoc(organizationRef);

            if (!organizationSnap.exists()) {
              return {
                error: {
                  status: 'NOT_FOUND',
                  data: 'Organization not found',
                },
              };
            }

            const orgData = organizationSnap.data();

            // Only update if bookingCustomerFields is not set
            if (!orgData.settings?.bookingCustomerFields) {
              await updateDoc(organizationRef, {
                'settings.bookingCustomerFields': getDefaultBookingCustomerFields(),
                updatedAt: serverTimestamp(),
              });
              updated = 1;
            }
          } else {
            // Migrate all organizations
            const organizationsRef = collection(DB, 'enhanced_organizations');
            const organizationsQuery = query(organizationsRef, where('isDeleted', '!=', true));
            const querySnapshot = await getDocs(organizationsQuery);

            const batch = writeBatch(DB);

            querySnapshot.docs.forEach((docSnap) => {
              const orgData = docSnap.data();

              // Only update if bookingCustomerFields is not set
              if (!orgData.settings?.bookingCustomerFields) {
                batch.update(docSnap.ref, {
                  'settings.bookingCustomerFields': getDefaultBookingCustomerFields(),
                  updatedAt: serverTimestamp(),
                });
                updated += 1;
              }
            });

            if (updated > 0) {
              await batch.commit();
            }
          }

          console.log(`Migrated ${updated} organization(s) with default booking customer fields`);

          return {
            data: {
              success: true,
              updated,
            },
          };
        } catch (error: any) {
          console.error('Error migrating organization booking fields:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data:
                error?.message || 'An error occurred while migrating organization booking fields',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => {
        if (organizationId) {
          return [
            { type: 'EnhancedOrganization', id: organizationId },
            { type: 'EnhancedOrganizations', id: 'LIST' },
          ];
        }
        return [{ type: 'EnhancedOrganizations', id: 'LIST' }];
      },
    }),

    // Check if organization name exists
    checkOrganizationNameExists: builder.query<
      { exists: boolean },
      { name: string; ownerId: string; excludeId?: string }
    >({
      async queryFn({ name, ownerId, excludeId }) {
        try {
          const organizationsRef = collection(DB, 'enhanced_organizations');
          const nameQuery = query(
            organizationsRef,
            where('name', '==', name),
            where('ownerId', '==', ownerId),
            where('isDeleted', '!=', true)
          );

          const querySnapshot = await getDocs(nameQuery);

          // If excluding an ID (for updates), filter it out
          const exists = querySnapshot.docs.some((doc) =>
            excludeId ? doc.id !== excludeId : true
          );

          return { data: { exists } };
        } catch (error: any) {
          console.error('Error checking organization name:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while checking organization name',
            },
          };
        }
      },
    }),

    // ==================== ROOMS MANAGEMENT ====================

    // Get all rooms for an organization
    getRoomsByOrganization: builder.query<RoomsResponse, string>({
      async queryFn(organizationId) {
        try {
          const roomsRef = collection(DB, 'rooms');
          const roomsQuery = query(
            roomsRef,
            where('organizationId', '==', organizationId),
            where('isDeleted', '!=', true)
          );

          const querySnapshot = await getDocs(roomsQuery);

          const rooms: Room[] = [];
          querySnapshot.forEach((doc) => {
            rooms.push({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
              updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
            } as Room);
          });

          return { data: { rooms } };
        } catch (error: any) {
          console.error('Error fetching rooms:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching rooms',
            },
          };
        }
      },
      providesTags: (result, error, organizationId) =>
        result
          ? [
              ...result.rooms.map(({ id }) => ({
                type: 'Room' as const,
                id,
              })),
              { type: 'Rooms', id: organizationId },
            ]
          : [{ type: 'Rooms', id: organizationId }],
    }),

    // Get single room
    getRoom: builder.query<RoomResponse, string>({
      async queryFn(roomId) {
        try {
          const roomRef = doc(DB, 'rooms', roomId);
          const docSnap = await getDoc(roomRef);

          if (docSnap.exists()) {
            const room = {
              id: docSnap.id,
              ...docSnap.data(),
              createdAt: docSnap.data().createdAt?.toDate?.() || docSnap.data().createdAt,
              updatedAt: docSnap.data().updatedAt?.toDate?.() || docSnap.data().updatedAt,
            } as Room;

            return { data: { room } };
          } else {
            return {
              error: {
                status: 'NOT_FOUND',
                data: `Room with ID ${roomId} not found`,
              },
            };
          }
        } catch (error: any) {
          console.error('Error fetching room:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching room',
            },
          };
        }
      },
      providesTags: (result, error, roomId) => [{ type: 'Room', id: roomId }],
    }),

    // Create room
    createRoom: builder.mutation<RoomResponse, CreateRoomRequest>({
      async queryFn(roomData) {
        try {
          const roomsRef = collection(DB, 'rooms');
          const docRef = await addDoc(roomsRef, {
            ...roomData,
            isDeleted: false,
            createdAt: serverTimestamp() as any,
            updatedAt: serverTimestamp() as any,
          });

          const createdRoom: Room = {
            id: docRef.id,
            ...roomData,
            isDeleted: false,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return { data: { room: createdRoom } };
        } catch (error: any) {
          console.error('Error creating room:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while creating room',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'Rooms', id: organizationId },
      ],
    }),

    // Update room
    updateRoom: builder.mutation<RoomResponse, UpdateRoomRequest & { id: string }>({
      async queryFn(roomData) {
        try {
          const { id, ...updateData } = roomData;
          const roomRef = doc(DB, 'rooms', id);

          await updateDoc(roomRef, {
            ...updateData,
            updatedAt: serverTimestamp(),
          });

          const updatedRoom: Room = {
            id,
            ...updateData,
            updatedAt: new Date(),
          } as Room;

          return { data: { room: updatedRoom } };
        } catch (error: any) {
          console.error('Error updating room:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while updating room',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Room', id },
        { type: 'Rooms', id: organizationId },
      ],
    }),

    // Soft delete room
    deleteRoom: builder.mutation<boolean, { id: string; organizationId: string }>({
      async queryFn({ id }) {
        try {
          const roomRef = doc(DB, 'rooms', id);
          await updateDoc(roomRef, {
            isDeleted: true,
            updatedAt: serverTimestamp(),
          });

          return { data: true };
        } catch (error: any) {
          console.error('Error deleting room:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while deleting room',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Room', id },
        { type: 'Rooms', id: organizationId },
      ],
    }),

    // Toggle room status (active/inactive)
    toggleRoomStatus: builder.mutation<
      RoomResponse,
      { id: string; organizationId: string; isActive: boolean }
    >({
      async queryFn({ id, isActive }) {
        try {
          const roomRef = doc(DB, 'rooms', id);
          await updateDoc(roomRef, {
            isActive,
            updatedAt: serverTimestamp(),
          });

          // Return minimal room data (will be invalidated and refetched)
          return { data: { room: { id, isActive } as Room } };
        } catch (error: any) {
          console.error('Error toggling room status:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while toggling room status',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Room', id },
        { type: 'Rooms', id: organizationId },
      ],
    }),

    // ==================== AVAILABILITY MANAGEMENT ====================

    // Get availability settings for an organization
    getAvailabilitySettings: builder.query<AvailabilitySettingsResponse, string>({
      async queryFn(organizationId) {
        try {
          console.log('[getAvailabilitySettings] Fetching for organization:', organizationId);

          const settingsRef = collection(DB, 'availability_settings');
          const settingsQuery = query(settingsRef, where('organizationId', '==', organizationId));
          const querySnapshot = await getDocs(settingsQuery);

          if (querySnapshot.empty) {
            console.log('[getAvailabilitySettings] No settings found, returning defaults');
            // Return default settings if none exist
            const { AvailabilityService } = await import('src/utils/availabilityService');
            const defaultSettings =
              AvailabilityService.getDefaultAvailabilitySettings(organizationId);
            return { data: { settings: defaultSettings } };
          }

          const docSnap = querySnapshot.docs[0];
          const docData = docSnap.data();

          console.log('[getAvailabilitySettings] Found settings document:', docSnap.id);
          console.log('[getAvailabilitySettings] Working hours from DB:', docData.workingHours);

          const settings = {
            ...docData,
            createdAt: docData.createdAt?.toDate?.() || docData.createdAt,
            updatedAt: docData.updatedAt?.toDate?.() || docData.updatedAt,
          } as AvailabilitySettings;

          console.log('[getAvailabilitySettings] Returning settings:', settings);

          return { data: { settings } };
        } catch (error: any) {
          console.error('Error fetching availability settings:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching availability settings',
            },
          };
        }
      },
      providesTags: (result, error, organizationId) => [
        { type: 'AvailabilitySettings', id: organizationId },
      ],
    }),

    // Create or update availability settings
    upsertAvailabilitySettings: builder.mutation<
      AvailabilitySettingsResponse,
      CreateAvailabilitySettingsRequest
    >({
      async queryFn(settingsData) {
        try {
          console.log('[upsertAvailabilitySettings] Saving settings:', settingsData);

          const settingsRef = collection(DB, 'availability_settings');
          const settingsQuery = query(
            settingsRef,
            where('organizationId', '==', settingsData.organizationId)
          );
          const querySnapshot = await getDocs(settingsQuery);

          // Prepare the data to save (flatten nested objects for Firestore)
          const dataToSave = {
            organizationId: settingsData.organizationId,
            workingHours: settingsData.workingHours,
            timeSlotDuration: settingsData.timeSlotDuration,
            timeSlotUnit: settingsData.timeSlotUnit,
            advanceBookingDays: settingsData.advanceBookingDays,
            minBookingNotice: settingsData.minBookingNotice,
            maxBookingDuration: settingsData.maxBookingDuration,
            holidayDates: settingsData.holidayDates || [],
            specialDates: settingsData.specialDates || [],
            autoApproveBookings: settingsData.autoApproveBookings,
            allowBackToBackBookings: settingsData.allowBackToBackBookings,
            bufferTimeBetweenBookings: settingsData.bufferTimeBetweenBookings,
          };

          let docRef;
          if (querySnapshot.empty) {
            // Create new settings
            console.log('[upsertAvailabilitySettings] Creating new document');
            docRef = await addDoc(settingsRef, {
              ...dataToSave,
              createdAt: serverTimestamp() as any,
              updatedAt: serverTimestamp() as any,
            });
          } else {
            // Update existing settings
            const existingDoc = querySnapshot.docs[0];
            docRef = existingDoc.ref;
            console.log('[upsertAvailabilitySettings] Updating existing document:', docRef.id);
            console.log(
              '[upsertAvailabilitySettings] Working hours to save:',
              dataToSave.workingHours
            );

            await updateDoc(docRef, {
              ...dataToSave,
              updatedAt: serverTimestamp(),
            });

            console.log('[upsertAvailabilitySettings] Update complete');
          }

          const settings = settingsData;

          return { data: { settings } };
        } catch (error: any) {
          console.error('Error upserting availability settings:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while saving availability settings',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'AvailabilitySettings', id: organizationId },
      ],
    }),

    // Get room-specific availability settings
    getRoomAvailability: builder.query<
      RoomAvailabilityResponse,
      { roomId: string; organizationId: string }
    >({
      async queryFn({ roomId, organizationId }) {
        try {
          const availabilityRef = collection(DB, 'room_availability');
          const availabilityQuery = query(
            availabilityRef,
            where('roomId', '==', roomId),
            where('organizationId', '==', organizationId)
          );

          const querySnapshot = await getDocs(availabilityQuery);

          if (querySnapshot.empty) {
            // Return default room availability
            const availability: RoomAvailability = {
              id: '',
              roomId,
              organizationId,
              isActive: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            return { data: { availability } };
          }

          const docSnap = querySnapshot.docs[0];
          const availability = {
            id: docSnap.id,
            ...docSnap.data(),
            createdAt: docSnap.data().createdAt?.toDate?.() || docSnap.data().createdAt,
            updatedAt: docSnap.data().updatedAt?.toDate?.() || docSnap.data().updatedAt,
          } as RoomAvailability;

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error fetching room availability:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching room availability',
            },
          };
        }
      },
      providesTags: (result, error, { roomId }) => [{ type: 'RoomAvailability', id: roomId }],
    }),

    // Create or update room availability
    upsertRoomAvailability: builder.mutation<
      RoomAvailabilityResponse,
      CreateRoomAvailabilityRequest
    >({
      async queryFn(availabilityData) {
        try {
          const availabilityRef = collection(DB, 'room_availability');
          const availabilityQuery = query(
            availabilityRef,
            where('roomId', '==', availabilityData.roomId),
            where('organizationId', '==', availabilityData.organizationId)
          );

          const querySnapshot = await getDocs(availabilityQuery);

          let docRef;
          if (querySnapshot.empty) {
            // Create new room availability
            docRef = await addDoc(availabilityRef, {
              ...availabilityData,
              createdAt: serverTimestamp() as any,
              updatedAt: serverTimestamp() as any,
            });
          } else {
            // Update existing room availability
            const existingDoc = querySnapshot.docs[0];
            docRef = existingDoc.ref;
            await updateDoc(docRef, {
              ...availabilityData,
              updatedAt: serverTimestamp(),
            });
          }

          const availability: RoomAvailability = {
            id: docRef.id,
            ...availabilityData,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error upserting room availability:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while saving room availability',
            },
          };
        }
      },
      invalidatesTags: (result, error, { roomId }) => [{ type: 'RoomAvailability', id: roomId }],
    }),

    // Get available time slots for a room on a specific date
    getAvailableTimeSlots: builder.query<
      GetAvailableTimeSlotsResponse,
      GetAvailableTimeSlotsRequest
    >({
      async queryFn({ organizationId, roomId, date, duration }) {
        try {
          // Import the availability service
          const { AvailabilityService } = await import('src/utils/availabilityService');

          // Get organization availability settings
          const settingsRef = collection(DB, 'availability_settings');
          const settingsQuery = query(settingsRef, where('organizationId', '==', organizationId));
          const settingsSnapshot = await getDocs(settingsQuery);

          let orgSettings: AvailabilitySettings;
          if (settingsSnapshot.empty) {
            orgSettings = AvailabilityService.getDefaultAvailabilitySettings(organizationId);
          } else {
            const settingsDoc = settingsSnapshot.docs[0];
            orgSettings = { ...settingsDoc.data() } as AvailabilitySettings;
          }

          // Get room-specific availability if exists
          const roomAvailabilityRef = collection(DB, 'room_availability');
          const roomQuery = query(
            roomAvailabilityRef,
            where('roomId', '==', roomId),
            where('organizationId', '==', organizationId)
          );
          const roomSnapshot = await getDocs(roomQuery);

          let roomAvailability: RoomAvailability | undefined;
          if (!roomSnapshot.empty) {
            const roomDoc = roomSnapshot.docs[0];
            roomAvailability = { id: roomDoc.id, ...roomDoc.data() } as RoomAvailability;
          }

          // Get effective working hours
          const effectiveWorkingHours = AvailabilityService.getEffectiveWorkingHours(
            orgSettings.workingHours,
            roomAvailability
          );

          // Generate available time slots
          const requestDate = new Date(date);
          const availableSlots = AvailabilityService.getAvailableTimeSlots(
            effectiveWorkingHours,
            requestDate,
            duration,
            orgSettings.timeSlotUnit,
            orgSettings.holidayDates,
            orgSettings.specialDates
          );

          // Get working hours for the specific day
          const days = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
          ] as const;
          const dayKey = days[requestDate.getDay()];
          const dayWorkingHours = AvailabilityService.getDayWorkingHours(
            effectiveWorkingHours,
            dayKey
          );

          return {
            data: {
              date,
              availableSlots,
              workingHours: {
                isWorkingDay: dayWorkingHours !== null,
                startTime: dayWorkingHours?.startTime,
                endTime: dayWorkingHours?.endTime,
              },
            },
          };
        } catch (error: any) {
          console.error('Error getting available time slots:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while getting available time slots',
            },
          };
        }
      },
    }),

    // === Booking Management Endpoints ===
    getBookings: builder.query<BookingsResponse, BookingFilters>({
      queryFn: async (filters) => {
        try {
          console.log('Fetching bookings with filters:', filters);

          const bookingsQuery = buildBookingsQuery(filters);
          const querySnapshot = await getDocs(bookingsQuery);

          const bookings: Booking[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            bookings.push({
              id: doc.id,
              roomId: data.listingId,
              ...data,
            } as Booking);
          });

          // Sort by creation date (newest first)
          bookings.sort((a, b) => {
            const aTime =
              a.createdAt instanceof Date
                ? a.createdAt.getTime()
                : (a.createdAt as any)?.seconds * 1000 || 0;
            const bTime =
              b.createdAt instanceof Date
                ? b.createdAt.getTime()
                : (b.createdAt as any)?.seconds * 1000 || 0;
            return bTime - aTime;
          });

          // Apply pagination
          const offset = filters.offset || 0;
          const limit = filters.limit || bookings.length;
          const paginatedBookings = bookings.slice(offset, offset + limit);

          return {
            data: {
              bookings: paginatedBookings,
              total: bookings.length,
            },
          };
        } catch (error) {
          console.error('Error fetching bookings:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },

      async onCacheEntryAdded(filters, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        await cacheDataLoaded;

        const bookingsQuery = buildBookingsQuery(filters);

        const unsubscribe = onSnapshot(
          bookingsQuery,
          (querySnapshot) => {
            const bookings: Booking[] = [];

            querySnapshot.forEach((doc) => {
              const data = doc.data();
              bookings.push({
                id: doc.id,
                roomId: data.listingId,
                ...data,
              } as Booking);
            });

            // Sort & paginate again
            bookings.sort((a, b) => {
              const aTime =
                a.createdAt instanceof Date
                  ? a.createdAt.getTime()
                  : (a.createdAt as any)?.seconds * 1000 || 0;
              const bTime =
                b.createdAt instanceof Date
                  ? b.createdAt.getTime()
                  : (b.createdAt as any)?.seconds * 1000 || 0;
              return bTime - aTime;
            });

            const offset = filters.offset || 0;
            const limit = filters.limit || bookings.length;
            const paginatedBookings = bookings.slice(offset, offset + limit);

            updateCachedData((draft) => {
              draft.bookings = paginatedBookings;
              draft.total = bookings.length;
            });
          },
          (error) => {
            console.error('Error in real-time bookings listener:', error);
          }
        );

        // Unsubscribe when cache is cleared
        await cacheEntryRemoved;
        unsubscribe();
      },

      providesTags: (result) =>
        result
          ? [
              ...result.bookings.map(({ id }) => ({ type: 'Booking' as const, id })),
              { type: 'Booking', id: 'LIST' },
            ]
          : [{ type: 'Booking', id: 'LIST' }],
    }),

    getBookingById: builder.query<BookingResponse, string>({
      queryFn: async (bookingId) => {
        try {
          const bookingDoc = await getDoc(doc(DB, 'bookings', bookingId));

          if (!bookingDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                data: 'Booking not found',
              },
            };
          }

          const data = bookingDoc.data();
          const booking: Booking = {
            id: bookingDoc.id,
            ...data,
          } as Booking;

          return { data: { booking } };
        } catch (error) {
          console.error('Error fetching booking:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      providesTags: (result, error, bookingId) => [{ type: 'Booking', id: bookingId }],
    }),

    createBooking: builder.mutation<BookingResponse, CreateBookingRequest>({
      queryFn: async (bookingData) => {
        try {
          console.log('Creating booking:', bookingData);

          // Strip recurrencePattern from the spread — we handle it separately
          const { recurrencePattern, ...rest } = bookingData;

          // Create the booking document
          const newBookingDoc = doc(collection(DB, 'bookings'));
          const booking: any = {
            ...rest,
            // Map roomId → listingId (Firestore convention) and keep roomId for compat
            listingId: bookingData.roomId,
            // Map engineerId → staffIds array (Firestore convention)
            staffIds: bookingData.engineerId ? [bookingData.engineerId] : [],
            startDateTime: Timestamp.fromDate(new Date(bookingData.startDateTime)),
            endDateTime: Timestamp.fromDate(new Date(bookingData.endDateTime)),
            status: bookingData.status || ('pending' as BookingStatus),
            paymentStatus: bookingData.paymentStatus || ('unpaid' as PaymentStatus),
            paidAmount: bookingData.paidAmount || 0,
            paymentMethodUsed: bookingData.paymentMethodUsed || 'pending',
            isDeleted: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            timeUnit: '1hr', // Default time unit
          };

          // Add recurrence metadata if present
          if (recurrencePattern) {
            booking.recurrencePattern = {
              ...recurrencePattern,
              ...(recurrencePattern.endDate
                ? { endDate: Timestamp.fromDate(new Date(recurrencePattern.endDate)) }
                : {}),
            };
          }

          await addDoc(collection(DB, 'bookings'), booking);

          const createdBooking: Booking = {
            id: newBookingDoc.id,
            ...booking,
            createdAt: new Date(),
            updatedAt: new Date(),
            startDateTime: new Date(bookingData.startDateTime),
            endDateTime: new Date(bookingData.endDateTime),
          };

          return { data: { booking: createdBooking } };
        } catch (error) {
          console.error('Error creating booking:', error);
          return {
            error: {
              status: 'CREATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: [
        { type: 'Booking', id: 'LIST' },
        { type: 'AvailableTimeSlots', id: 'LIST' },
      ],
    }),

    updateBooking: builder.mutation<BookingResponse, UpdateBookingRequest>({
      queryFn: async (updateData) => {
        try {
          const { id, rescheduleEntry, ...updates } = updateData;
          const bookingRef = doc(DB, 'bookings', id);

          // Prepare update data
          const updatePayload: any = {
            ...updates,
            updatedAt: serverTimestamp(),
          };

          // Convert date strings to Timestamps if present
          if (updates.startDateTime) {
            updatePayload.startDateTime = Timestamp.fromDate(new Date(updates.startDateTime));
          }
          if (updates.endDateTime) {
            updatePayload.endDateTime = Timestamp.fromDate(new Date(updates.endDateTime));
          }

          // Append rescheduling history entry if present
          if (rescheduleEntry) {
            updatePayload.rescheduleHistory = arrayUnion({
              previousStartDateTime: Timestamp.fromDate(new Date(rescheduleEntry.previousStartDateTime)),
              previousEndDateTime: Timestamp.fromDate(new Date(rescheduleEntry.previousEndDateTime)),
              newStartDateTime: Timestamp.fromDate(new Date(rescheduleEntry.newStartDateTime)),
              newEndDateTime: Timestamp.fromDate(new Date(rescheduleEntry.newEndDateTime)),
              rescheduledAt: Timestamp.fromDate(new Date()),
              rescheduledBy: rescheduleEntry.rescheduledBy || null,
            });
            updatePayload.lastRescheduledAt = serverTimestamp();
          }

          await updateDoc(bookingRef, updatePayload);

          // Fetch updated booking
          const updatedDoc = await getDoc(bookingRef);
          if (!updatedDoc.exists()) {
            throw new Error('Booking not found after update');
          }

          const booking: Booking = {
            id: updatedDoc.id,
            ...updatedDoc.data(),
          } as Booking;

          return { data: { booking } };
        } catch (error) {
          console.error('Error updating booking:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Booking', id },
        { type: 'Booking', id: 'LIST' },
        { type: 'AvailableTimeSlots', id: 'LIST' },
      ],
    }),

    deleteBooking: builder.mutation<{ success: boolean }, string>({
      queryFn: async (bookingId) => {
        try {
          // Soft delete - mark as deleted instead of actually deleting
          const bookingRef = doc(DB, 'bookings', bookingId);
          await updateDoc(bookingRef, {
            isDeleted: true,
            updatedAt: serverTimestamp(),
          });
          return { data: { success: true } };
        } catch (error) {
          console.error('Error deleting booking:', error);
          return {
            error: {
              status: 'DELETE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, bookingId) => [
        { type: 'Booking', id: bookingId },
        { type: 'Booking', id: 'LIST' },
        { type: 'AvailableTimeSlots', id: 'LIST' },
      ],
    }),

    // === Equipment Management Endpoints ===
    getEquipmentByOrganization: builder.query<EquipmentListResponse, EquipmentFilters>({
      queryFn: async (filters) => {
        try {
          console.log('Getting equipment with filters:', filters);

          let equipmentQuery = query(
            collection(DB, 'studio_equipment'),
            where('organizationId', '==', filters.organizationId),
            where('isDeleted', '!=', true),
            orderBy('isDeleted'),
            orderBy('createdAt', 'desc')
          );

          // Apply filters
          if (filters.type) {
            if (Array.isArray(filters.type)) {
              equipmentQuery = query(equipmentQuery, where('type', 'in', filters.type));
            } else {
              equipmentQuery = query(equipmentQuery, where('type', '==', filters.type));
            }
          }

          if (filters.available !== undefined) {
            equipmentQuery = query(equipmentQuery, where('available', '==', filters.available));
          }

          if (filters.condition) {
            equipmentQuery = query(equipmentQuery, where('condition', '==', filters.condition));
          }

          if (filters.location) {
            equipmentQuery = query(equipmentQuery, where('location', '==', filters.location));
          }

          const querySnapshot = await getDocs(equipmentQuery);
          let equipment: Equipment[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            equipment.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || data.createdAt,
              updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
              purchaseDate: data.purchaseDate?.toDate?.() || data.purchaseDate,
              lastMaintenanceDate: data.lastMaintenanceDate?.toDate?.() || data.lastMaintenanceDate,
              nextMaintenanceDate: data.nextMaintenanceDate?.toDate?.() || data.nextMaintenanceDate,
              warrantyExpires: data.warrantyExpires?.toDate?.() || data.warrantyExpires,
            } as Equipment);
          });

          // Apply search filter if provided
          if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            equipment = equipment.filter(
              (item) =>
                item.name.toLowerCase().includes(searchTerm) ||
                item.brand?.toLowerCase().includes(searchTerm) ||
                item.model?.toLowerCase().includes(searchTerm) ||
                item.description?.toLowerCase().includes(searchTerm)
            );
          }

          // Sort by name
          equipment.sort((a, b) => a.name.localeCompare(b.name));

          // Apply pagination
          const offset = filters.offset || 0;
          const limit = filters.limit || equipment.length;
          const paginatedEquipment = equipment.slice(offset, offset + limit);

          return {
            data: {
              equipment: paginatedEquipment,
              total: equipment.length,
            },
          };
        } catch (error) {
          console.error('Error fetching equipment:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      providesTags: (result, error, { organizationId }) =>
        result
          ? [
              ...result.equipment.map(({ id }) => ({ type: 'Equipment' as const, id })),
              { type: 'EquipmentList', id: organizationId },
            ]
          : [{ type: 'EquipmentList', id: organizationId }],
    }),

    getEquipmentById: builder.query<EquipmentResponse, string>({
      queryFn: async (equipmentId) => {
        try {
          const equipmentDoc = await getDoc(doc(DB, 'studio_equipment', equipmentId));

          if (!equipmentDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                data: 'Equipment not found',
              },
            };
          }

          const data = equipmentDoc.data();
          const equipment: Equipment = {
            id: equipmentDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
            purchaseDate: data.purchaseDate?.toDate?.() || data.purchaseDate,
            lastMaintenanceDate: data.lastMaintenanceDate?.toDate?.() || data.lastMaintenanceDate,
            nextMaintenanceDate: data.nextMaintenanceDate?.toDate?.() || data.nextMaintenanceDate,
            warrantyExpires: data.warrantyExpires?.toDate?.() || data.warrantyExpires,
          } as Equipment;

          return { data: { equipment } };
        } catch (error) {
          console.error('Error fetching equipment:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      providesTags: (result, error, equipmentId) => [{ type: 'Equipment', id: equipmentId }],
    }),

    createEquipment: builder.mutation<EquipmentResponse, CreateEquipmentRequest>({
      queryFn: async (equipmentData) => {
        try {
          console.log('Creating equipment:', equipmentData);

          // Create the equipment document
          const equipmentPayload: any = {
            ...equipmentData,
            available: equipmentData.available ?? true,
            condition: equipmentData.condition || 'Good',
            isDeleted: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          // Convert date strings to Timestamps if present
          if (equipmentData.purchaseDate) {
            equipmentPayload.purchaseDate = Timestamp.fromDate(
              new Date(equipmentData.purchaseDate)
            );
          }
          if (equipmentData.warrantyExpires) {
            equipmentPayload.warrantyExpires = Timestamp.fromDate(
              new Date(equipmentData.warrantyExpires)
            );
          }

          const docRef = await addDoc(collection(DB, 'studio_equipment'), equipmentPayload);

          const createdEquipment: Equipment = {
            id: docRef.id,
            ...equipmentPayload,
            createdAt: new Date(),
            updatedAt: new Date(),
            purchaseDate: equipmentData.purchaseDate
              ? new Date(equipmentData.purchaseDate)
              : undefined,
            warrantyExpires: equipmentData.warrantyExpires
              ? new Date(equipmentData.warrantyExpires)
              : undefined,
          };

          return { data: { equipment: createdEquipment } };
        } catch (error) {
          console.error('Error creating equipment:', error);
          return {
            error: {
              status: 'CREATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'EquipmentList', id: organizationId },
      ],
    }),

    updateEquipment: builder.mutation<EquipmentResponse, UpdateEquipmentRequest>({
      queryFn: async (updateData) => {
        try {
          const { id, ...updates } = updateData;
          const equipmentRef = doc(DB, 'studio_equipment', id);

          // Prepare update data
          const updatePayload: any = {
            ...updates,
            updatedAt: serverTimestamp(),
          };

          // Convert date strings to Timestamps if present
          if (updates.purchaseDate) {
            updatePayload.purchaseDate = Timestamp.fromDate(new Date(updates.purchaseDate));
          }
          if (updates.warrantyExpires) {
            updatePayload.warrantyExpires = Timestamp.fromDate(new Date(updates.warrantyExpires));
          }

          await updateDoc(equipmentRef, updatePayload);

          // Fetch updated equipment
          const updatedDoc = await getDoc(equipmentRef);
          if (!updatedDoc.exists()) {
            throw new Error('Equipment not found after update');
          }

          const data = updatedDoc.data();
          const equipment: Equipment = {
            id: updatedDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
            purchaseDate: data.purchaseDate?.toDate?.() || data.purchaseDate,
            lastMaintenanceDate: data.lastMaintenanceDate?.toDate?.() || data.lastMaintenanceDate,
            nextMaintenanceDate: data.nextMaintenanceDate?.toDate?.() || data.nextMaintenanceDate,
            warrantyExpires: data.warrantyExpires?.toDate?.() || data.warrantyExpires,
          } as Equipment;

          return { data: { equipment } };
        } catch (error) {
          console.error('Error updating equipment:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Equipment', id },
        { type: 'EquipmentList', id: organizationId },
      ],
    }),

    deleteEquipment: builder.mutation<{ success: boolean }, { id: string; organizationId: string }>(
      {
        queryFn: async ({ id }) => {
          try {
            // Soft delete - mark as deleted instead of actually deleting
            const equipmentRef = doc(DB, 'studio_equipment', id);
            await updateDoc(equipmentRef, {
              isDeleted: true,
              updatedAt: serverTimestamp(),
            });
            return { data: { success: true } };
          } catch (error) {
            console.error('Error deleting equipment:', error);
            return {
              error: {
                status: 'DELETE_ERROR',
                data: error instanceof Error ? error.message : 'Unknown error occurred',
              },
            };
          }
        },
        invalidatesTags: (result, error, { id, organizationId }) => [
          { type: 'Equipment', id },
          { type: 'EquipmentList', id: organizationId },
        ],
      }
    ),

    toggleEquipmentAvailability: builder.mutation<
      EquipmentResponse,
      { id: string; organizationId: string; available: boolean }
    >({
      queryFn: async ({ id, available }) => {
        try {
          const equipmentRef = doc(DB, 'studio_equipment', id);
          await updateDoc(equipmentRef, {
            available,
            updatedAt: serverTimestamp(),
          });

          // Fetch updated equipment
          const updatedDoc = await getDoc(equipmentRef);
          if (!updatedDoc.exists()) {
            throw new Error('Equipment not found after update');
          }

          const data = updatedDoc.data();
          const equipment: Equipment = {
            id: updatedDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
            purchaseDate: data.purchaseDate?.toDate?.() || data.purchaseDate,
            lastMaintenanceDate: data.lastMaintenanceDate?.toDate?.() || data.lastMaintenanceDate,
            nextMaintenanceDate: data.nextMaintenanceDate?.toDate?.() || data.nextMaintenanceDate,
            warrantyExpires: data.warrantyExpires?.toDate?.() || data.warrantyExpires,
          } as Equipment;

          return { data: { equipment } };
        } catch (error) {
          console.error('Error toggling equipment availability:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Equipment', id },
        { type: 'EquipmentList', id: organizationId },
      ],
    }),

    // Bridge query to get legacy music studio organizations for enhanced service management
    getLegacyEnhancedOrganization: builder.query<FetchEnhancedOrganizationResponse, string>({
      async queryFn(organizationId) {
        try {
          // First try to get from enhanced collection
          const enhancedOrgRef = doc(DB, 'enhanced_organizations', organizationId);
          const enhancedDocSnap = await getDoc(enhancedOrgRef);

          if (enhancedDocSnap.exists()) {
            const organization = {
              id: enhancedDocSnap.id,
              ...enhancedDocSnap.data(),
              createdAt:
                enhancedDocSnap.data().createdAt?.toDate?.() || enhancedDocSnap.data().createdAt,
              updatedAt:
                enhancedDocSnap.data().updatedAt?.toDate?.() || enhancedDocSnap.data().updatedAt,
            } as EnhancedOrganization;

            return { data: { organization } };
          }

          // If not found, try legacy organizations collection for music studios
          const legacyOrgRef = doc(DB, 'organisers', organizationId);
          const legacyDocSnap = await getDoc(legacyOrgRef);

          if (legacyDocSnap.exists()) {
            const legacyOrg = legacyDocSnap.data();

            // Only allow music studios from legacy system
            if (legacyOrg.type === 'recording_studio') {
              // Transform legacy organization to enhanced format
              const enhancedOrganization: EnhancedOrganization = {
                id: legacyDocSnap.id,
                name: legacyOrg.name,
                description: legacyOrg.bio || '',
                bio: legacyOrg.bio,
                type: 'recording_studio' as const,
                ownerId: legacyOrg.ownerid,
                ownerid: legacyOrg.ownerid,
                language: legacyOrg.language || 'fr',
                logo: legacyOrg.logoImage,
                logoImage: legacyOrg.logoImage,
                coverImage: legacyOrg.coverImage,
                contact: legacyOrg.contact,
                settings: legacyOrg.settings || getDefaultSettingsForType('recording_studio'),
                followers: legacyOrg.followers,
                totalEvents: legacyOrg.totalEvents,
                address: legacyOrg.address,
                legalInfos: legacyOrg.legalInfos,
                siren: legacyOrg.siren,
                siret: legacyOrg.siret,
                companyNumber: legacyOrg.companyNumber,
                groupeTable: legacyOrg.groupeTable,
                isDeleted: legacyOrg.isDeleted || false,
                createdAt: new Date(),
                updatedAt: new Date(),
              };

              return { data: { organization: enhancedOrganization } };
            }
          }

          return {
            error: {
              status: 'NOT_FOUND',
              data: `Organization with ID ${organizationId} not found or not a music studio`,
            },
          };
        } catch (error: any) {
          console.error('Error fetching legacy enhanced organization:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organization',
            },
          };
        }
      },
      providesTags: (result, error, organizationId) => [
        { type: 'EnhancedOrganization', id: organizationId },
      ],
    }),

    // ==================== MUSIC SHOWCASE MANAGEMENT ====================

    // Get music showcase for an organization
    getMusicShowcase: builder.query<MusicShowcaseResponse, string>({
      async queryFn(organizationId) {
        try {
          const showcaseRef = collection(DB, 'music_showcases');
          const showcaseQuery = query(showcaseRef, where('organizationId', '==', organizationId));
          const querySnapshot = await getDocs(showcaseQuery);

          if (querySnapshot.empty) {
            // Return default showcase if none exists
            const defaultShowcase: MusicShowcase = {
              organizationId,
              isEnabled: false,
              tracks: [],
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            return { data: { showcase: defaultShowcase } };
          }

          const docSnap = querySnapshot.docs[0];
          const showcaseData = docSnap.data();

          // Get tracks for this showcase
          const tracksRef = collection(DB, 'music_tracks');
          const tracksQuery = query(
            tracksRef,
            where('organizationId', '==', organizationId),
            where('isActive', '==', true)
          );
          const tracksSnapshot = await getDocs(tracksQuery);

          const tracks: MusicTrack[] = [];
          tracksSnapshot.forEach((trackDoc) => {
            const trackData = trackDoc.data();
            tracks.push({
              id: trackDoc.id,
              ...trackData,
              createdAt: trackData.createdAt?.toDate?.() || trackData.createdAt,
              updatedAt: trackData.updatedAt?.toDate?.() || trackData.updatedAt,
            } as MusicTrack);
          });

          // Sort tracks by order
          tracks.sort((a, b) => a.order - b.order);

          const showcase: MusicShowcase = {
            id: docSnap.id,
            organizationId,
            isEnabled: showcaseData.isEnabled,
            title: showcaseData.title,
            description: showcaseData.description,
            tracks,
            createdAt: showcaseData.createdAt?.toDate?.() || showcaseData.createdAt,
            updatedAt: showcaseData.updatedAt?.toDate?.() || showcaseData.updatedAt,
          };

          return { data: { showcase } };
        } catch (error: any) {
          console.error('Error fetching music showcase:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching music showcase',
            },
          };
        }
      },
      providesTags: (result, error, organizationId) => [
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Create or update music showcase
    upsertMusicShowcase: builder.mutation<MusicShowcaseResponse, CreateMusicShowcaseRequest>({
      async queryFn(showcaseData) {
        try {
          const showcaseRef = collection(DB, 'music_showcases');
          const showcaseQuery = query(
            showcaseRef,
            where('organizationId', '==', showcaseData.organizationId)
          );
          const querySnapshot = await getDocs(showcaseQuery);

          let docRef;
          if (querySnapshot.empty) {
            // Create new showcase
            docRef = await addDoc(showcaseRef, {
              ...showcaseData,
              createdAt: serverTimestamp() as any,
              updatedAt: serverTimestamp() as any,
            });
          } else {
            // Update existing showcase - preserve existing fields
            const existingDoc = querySnapshot.docs[0];
            docRef = existingDoc.ref;
            const existingData = existingDoc.data();

            await updateDoc(docRef, {
              // Only update the fields that are provided, preserve others
              organizationId: showcaseData.organizationId,
              isEnabled: showcaseData.isEnabled,
              ...(showcaseData.title !== undefined && { title: showcaseData.title }),
              ...(showcaseData.description !== undefined && {
                description: showcaseData.description,
              }),
              updatedAt: serverTimestamp(),
            });
          }

          const showcase: MusicShowcase = {
            id: docRef.id,
            ...showcaseData,
            tracks: [],
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return { data: { showcase } };
        } catch (error: any) {
          console.error('Error upserting music showcase:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while saving music showcase',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Get music tracks for an organization
    getMusicTracks: builder.query<MusicTracksResponse, MusicTrackFilters>({
      async queryFn(filters) {
        try {
          console.log('Getting music tracks with filters:', filters);

          let tracksQuery = query(collection(DB, 'music_tracks'));

          // Apply filters
          if (filters.organizationId) {
            tracksQuery = query(tracksQuery, where('organizationId', '==', filters.organizationId));
          }

          if (filters.showcaseId) {
            tracksQuery = query(tracksQuery, where('showcaseId', '==', filters.showcaseId));
          }

          if (filters.platform) {
            if (Array.isArray(filters.platform)) {
              tracksQuery = query(tracksQuery, where('platform', 'in', filters.platform));
            } else {
              tracksQuery = query(tracksQuery, where('platform', '==', filters.platform));
            }
          }

          if (filters.isActive !== undefined) {
            tracksQuery = query(tracksQuery, where('isActive', '==', filters.isActive));
          }

          const querySnapshot = await getDocs(tracksQuery);
          let tracks: MusicTrack[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            tracks.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || data.createdAt,
              updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
            } as MusicTrack);
          });

          // Apply search filter if provided
          if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            tracks = tracks.filter(
              (track) =>
                track.title.toLowerCase().includes(searchTerm) ||
                track.artist?.toLowerCase().includes(searchTerm) ||
                track.description?.toLowerCase().includes(searchTerm)
            );
          }

          // Sort by order
          tracks.sort((a, b) => a.order - b.order);

          // Apply pagination
          const offset = filters.offset || 0;
          const limit = filters.limit || tracks.length;
          const paginatedTracks = tracks.slice(offset, offset + limit);

          return {
            data: {
              tracks: paginatedTracks,
              total: tracks.length,
            },
          };
        } catch (error) {
          console.error('Error fetching music tracks:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      providesTags: (result, error, { organizationId }) =>
        result
          ? [
              ...result.tracks.map(({ id }) => ({ type: 'MusicTrack' as const, id })),
              { type: 'MusicTracks', id: organizationId },
            ]
          : [{ type: 'MusicTracks', id: organizationId }],
    }),

    // Get single music track
    getMusicTrack: builder.query<MusicTrackResponse, string>({
      async queryFn(trackId) {
        try {
          const trackDoc = await getDoc(doc(DB, 'music_tracks', trackId));

          if (!trackDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                data: 'Music track not found',
              },
            };
          }

          const data = trackDoc.data();
          const track: MusicTrack = {
            id: trackDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          } as MusicTrack;

          return { data: { track } };
        } catch (error) {
          console.error('Error fetching music track:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      providesTags: (result, error, trackId) => [{ type: 'MusicTrack', id: trackId }],
    }),

    // Create music track
    createMusicTrack: builder.mutation<MusicTrackResponse, CreateMusicTrackRequest>({
      async queryFn(trackData) {
        try {
          console.log('Creating music track:', trackData);

          // Validate URL format based on platform
          if (!validateMusicUrl(trackData.url, trackData.platform)) {
            return {
              error: {
                status: 'INVALID_URL',
                data: `Invalid URL format for ${trackData.platform}`,
              },
            };
          }

          // Generate embed code if possible
          const embedCode = generateEmbedCode(trackData.url, trackData.platform);

          // If no order specified, get the next order number
          let order = trackData.order || 0;
          if (order === 0) {
            const tracksRef = collection(DB, 'music_tracks');
            const tracksQuery = query(
              tracksRef,
              where('organizationId', '==', trackData.organizationId)
            );
            const tracksSnapshot = await getDocs(tracksQuery);
            order = tracksSnapshot.size + 1;
          }

          // Create the track document
          const trackPayload: any = {
            ...trackData,
            order,
            embedCode,
            isActive: trackData.isActive ?? true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          const docRef = await addDoc(collection(DB, 'music_tracks'), trackPayload);

          const createdTrack: MusicTrack = {
            id: docRef.id,
            ...trackPayload,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return { data: { track: createdTrack } };
        } catch (error) {
          console.error('Error creating music track:', error);
          return {
            error: {
              status: 'CREATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'MusicTracks', id: organizationId },
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Update music track
    updateMusicTrack: builder.mutation<MusicTrackResponse, UpdateMusicTrackRequest>({
      async queryFn(updateData) {
        try {
          const { id, ...updates } = updateData;
          const trackRef = doc(DB, 'music_tracks', id);

          // Validate URL format if URL is being updated
          if (updates.url && updates.platform) {
            if (!validateMusicUrl(updates.url, updates.platform)) {
              return {
                error: {
                  status: 'INVALID_URL',
                  data: `Invalid URL format for ${updates.platform}`,
                },
              };
            }
          }

          // Prepare update data
          const updatePayload: any = {
            ...updates,
            updatedAt: serverTimestamp(),
          };

          // Generate new embed code if URL or platform changed
          if (updates.url || updates.platform) {
            // Get current track to merge platform/url if only one is being updated
            const currentDoc = await getDoc(trackRef);
            if (currentDoc.exists()) {
              const currentData = currentDoc.data();
              const finalUrl = updates.url || currentData.url;
              const finalPlatform = updates.platform || currentData.platform;
              updatePayload.embedCode = generateEmbedCode(finalUrl, finalPlatform);
            }
          }

          await updateDoc(trackRef, updatePayload);

          // Fetch updated track
          const updatedDoc = await getDoc(trackRef);
          if (!updatedDoc.exists()) {
            throw new Error('Music track not found after update');
          }

          const data = updatedDoc.data();
          const track: MusicTrack = {
            id: updatedDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          } as MusicTrack;

          return { data: { track } };
        } catch (error) {
          console.error('Error updating music track:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'MusicTrack', id },
        { type: 'MusicTracks', id: organizationId },
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Delete music track (soft delete)
    deleteMusicTrack: builder.mutation<
      { success: boolean },
      { id: string; organizationId: string }
    >({
      async queryFn({ id }) {
        try {
          const trackRef = doc(DB, 'music_tracks', id);
          await updateDoc(trackRef, {
            isActive: false,
            updatedAt: serverTimestamp(),
          });
          return { data: { success: true } };
        } catch (error) {
          console.error('Error deleting music track:', error);
          return {
            error: {
              status: 'DELETE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'MusicTrack', id },
        { type: 'MusicTracks', id: organizationId },
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Reorder music tracks
    reorderMusicTracks: builder.mutation<
      { success: boolean },
      { organizationId: string; trackIds: string[] }
    >({
      async queryFn({ organizationId, trackIds }) {
        try {
          const batch = writeBatch(DB);

          trackIds.forEach((trackId, index) => {
            const trackRef = doc(DB, 'music_tracks', trackId);
            batch.update(trackRef, {
              order: index + 1,
              updatedAt: serverTimestamp(),
            });
          });

          await batch.commit();
          return { data: { success: true } };
        } catch (error) {
          console.error('Error reordering music tracks:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'MusicTracks', id: organizationId },
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),

    // Toggle track status (active/inactive)
    toggleMusicTrackStatus: builder.mutation<
      MusicTrackResponse,
      { id: string; organizationId: string; isActive: boolean }
    >({
      async queryFn({ id, isActive }) {
        try {
          const trackRef = doc(DB, 'music_tracks', id);
          await updateDoc(trackRef, {
            isActive,
            updatedAt: serverTimestamp(),
          });

          // Fetch updated track
          const updatedDoc = await getDoc(trackRef);
          if (!updatedDoc.exists()) {
            throw new Error('Music track not found after update');
          }

          const data = updatedDoc.data();
          const track: MusicTrack = {
            id: updatedDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          } as MusicTrack;

          return { data: { track } };
        } catch (error) {
          console.error('Error toggling music track status:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error occurred',
            },
          };
        }
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'MusicTrack', id },
        { type: 'MusicTracks', id: organizationId },
        { type: 'MusicShowcase', id: organizationId },
      ],
    }),
  }),
});

// Helper function to get default settings for organization type
function getDefaultSettingsForType(type: EnhancedOrgType): OrganizationSettings {
  // getDefaultBookingCustomerFields is imported at the top of the file

  const baseSettings: OrganizationSettings = {
    enabledFeatures: ['basic'],
    businessHours: {
      monday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
      tuesday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
      wednesday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
      thursday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
      friday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
      saturday: { isWorking: false },
      sunday: { isWorking: false },
    },
    bookingRules: {
      timeIncrement: 60,
      advanceBookingDays: 30,
      maxBookingDuration: 480,
      requireStaffSelection: false,
      allowMultipleBookings: true,
    },
    bookingCustomerFields: getDefaultBookingCustomerFields(),
    notifications: {
      email: {
        isActive: true, // Email channel enabled by default
        bookingConfirmation: true, // Notify client after booking is completed
        bookingReminder: true, // Notify client to confirm booking
        bookingStatusUpdate: true, // Notify client if booking status changes
        ownerBookingAlert: true, // Notify owner of new booking
        ownerCancellationAlert: true, // Notify owner of cancelled booking
      },
      sms: {
        isActive: false, // SMS channel disabled by default
        bookingConfirmation: false,
        bookingReminder: false,
        bookingStatusUpdate: false,
        ownerBookingAlert: false,
        ownerCancellationAlert: false,
      },
      whatsapp: {
        isActive: false, // WhatsApp channel disabled by default
        bookingConfirmation: false,
        bookingReminder: false,
        bookingStatusUpdate: false,
        ownerBookingAlert: false,
        ownerCancellationAlert: false,
      },
    },
  };

  // Customize settings based on organization type
  switch (type) {
    case 'evenementiel':
      return {
        ...baseSettings,
        enabledFeatures: ['events', 'tickets', 'reservations', 'analytics'],
        bookingRules: {
          ...baseSettings.bookingRules,
          timeIncrement: 30,
          requireStaffSelection: true,
        },
      };
    case 'restaurant':
      return {
        ...baseSettings,
        enabledFeatures: ['tables', 'menus', 'orders', 'reservations'],
        bookingRules: {
          ...baseSettings.bookingRules,
          timeIncrement: 15,
          maxBookingDuration: 180,
        },
      };
    case 'recording_studio':
      return {
        ...baseSettings,
        enabledFeatures: ['rooms', 'engineers', 'recording', 'equipment_rental', 'calendar'],
        businessHours: {
          monday: { isWorking: true, startTime: '09:00', endTime: '22:00' },
          tuesday: { isWorking: true, startTime: '09:00', endTime: '22:00' },
          wednesday: { isWorking: true, startTime: '09:00', endTime: '22:00' },
          thursday: { isWorking: true, startTime: '09:00', endTime: '22:00' },
          friday: { isWorking: true, startTime: '09:00', endTime: '23:00' },
          saturday: { isWorking: true, startTime: '10:00', endTime: '23:00' },
          sunday: { isWorking: true, startTime: '12:00', endTime: '20:00' },
        },
        bookingRules: {
          ...baseSettings.bookingRules,
          timeIncrement: 60, // Hour-based booking for recording sessions
          requireStaffSelection: true, // Need sound engineer
          allowMultipleBookings: true, // Multiple rooms can be booked
          advanceBookingDays: 60, // Longer advance booking for studio sessions
          maxBookingDuration: 720, // Up to 12 hours for long sessions
        },
        notifications: {
          ...baseSettings.notifications,
        },
      };
    case 'service':
      return {
        ...baseSettings,
        enabledFeatures: ['appointments', 'staff', 'services', 'calendar'],
        bookingRules: {
          ...baseSettings.bookingRules,
          timeIncrement: 30,
          requireStaffSelection: true,
        },
      };
    case 'healthcare':
      return {
        ...baseSettings,
        enabledFeatures: ['appointments', 'patients', 'medical_records', 'staff'],
        bookingRules: {
          ...baseSettings.bookingRules,
          timeIncrement: 15,
          requireStaffSelection: true,
          allowMultipleBookings: false,
        },
      };
    case 'beauty':
      return {
        ...baseSettings,
        enabledFeatures: ['appointments', 'services', 'staff', 'products'],
        businessHours: {
          monday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
          tuesday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
          wednesday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
          thursday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
          friday: { isWorking: true, startTime: '09:00', endTime: '17:00' },
          saturday: { isWorking: true, startTime: '09:00', endTime: '18:00' },
          sunday: { isWorking: false },
        },
      };
    default:
      return baseSettings;
  }
}

// Export hooks for use in components
export const {
  useGetEnhancedOrganizationsQuery,
  useGetOrganizationsByTypeQuery,
  useGetEnhancedOrganizationQuery,
  useGetLegacyEnhancedOrganizationQuery,
  useCompleteOrganizationOnboardingMutation,
  useCreateEnhancedOrganizationMutation,
  useUpdateEnhancedOrganizationMutation,
  useDeleteEnhancedOrganizationMutation,
  useMigrateOrganizationBookingFieldsMutation,
  useCheckOrganizationNameExistsQuery,
  // Room management hooks
  useGetRoomsByOrganizationQuery,
  useGetRoomQuery,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
  useToggleRoomStatusMutation,
  // Availability management hooks
  useGetAvailabilitySettingsQuery,
  useUpsertAvailabilitySettingsMutation,
  useGetRoomAvailabilityQuery,
  useUpsertRoomAvailabilityMutation,
  useGetAvailableTimeSlotsQuery,
  // Booking management hooks
  useGetBookingsQuery,
  useGetBookingByIdQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useDeleteBookingMutation,
  // Equipment management hooks
  useGetEquipmentByOrganizationQuery,
  useGetEquipmentByIdQuery,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useToggleEquipmentAvailabilityMutation,
  // Music showcase management hooks
  useGetMusicShowcaseQuery,
  useUpsertMusicShowcaseMutation,
  useGetMusicTracksQuery,
  useGetMusicTrackQuery,
  useCreateMusicTrackMutation,
  useUpdateMusicTrackMutation,
  useDeleteMusicTrackMutation,
  useReorderMusicTracksMutation,
  useToggleMusicTrackStatusMutation,
} = EnhancedOrganizationsApi;

// Organization presets for different types
export const ORGANIZATION_PRESETS: Record<EnhancedOrgType, OrganizationPreset> = {
  evenementiel: {
    type: 'evenementiel',
    label: 'Events & Entertainment',
    description: 'Event planning, venues, concerts, conferences',
    icon: 'mdi:party-popper',
    color: '#8B5CF6',
    category: 'existing',
    defaultSettings: getDefaultSettingsForType('evenementiel'),
    defaultRoles: ['Event Manager', 'Coordinator', 'Security'],
    requiredFeatures: ['events', 'tickets', 'reservations'],
    optionalFeatures: ['promotions', 'seating', 'analytics', 'reports'],
  },
  restaurant: {
    type: 'restaurant',
    label: 'Restaurant & Food Service',
    description: 'Restaurants, cafes, bars, catering services',
    icon: 'mdi:silverware-fork-knife',
    color: '#F59E0B',
    category: 'existing',
    defaultSettings: getDefaultSettingsForType('restaurant'),
    defaultRoles: ['Manager', 'Chef', 'Waiter', 'Host'],
    requiredFeatures: ['tables', 'menus', 'orders'],
    optionalFeatures: ['inventory', 'pos', 'reservations', 'analytics'],
  },
  recording_studio: {
    type: 'recording_studio',
    label: 'Music/Recording Studios',
    description: 'Recording studios, music production, audio engineering',
    icon: 'mdi:microphone',
    color: '#DC2626',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('recording_studio'),
    defaultRoles: ['Sound Engineer', 'Producer', 'Studio Manager', 'Assistant'],
    requiredFeatures: ['rooms', 'engineers', 'recording', 'equipment_rental'],
    optionalFeatures: ['mixing', 'mastering', 'supported_services', 'analytics'],
  },
  service: {
    type: 'service',
    label: 'Professional Services',
    description: 'Consulting, legal, accounting, repairs',
    icon: 'mdi:briefcase',
    color: '#3B82F6',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('service'),
    defaultRoles: ['Consultant', 'Specialist', 'Assistant'],
    requiredFeatures: ['appointments', 'staff', 'services'],
    optionalFeatures: ['clients', 'invoicing', 'calendar', 'analytics'],
  },
  healthcare: {
    type: 'healthcare',
    label: 'Healthcare & Medical',
    description: 'Clinics, hospitals, dental practices',
    icon: 'mdi:medical-bag',
    color: '#EF4444',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('healthcare'),
    defaultRoles: ['Doctor', 'Nurse', 'Receptionist', 'Technician'],
    requiredFeatures: ['appointments', 'patients', 'medical_records'],
    optionalFeatures: ['prescriptions', 'calendar', 'reports', 'analytics'],
  },
  beauty: {
    type: 'beauty',
    label: 'Beauty & Wellness',
    description: 'Hair salons, spas, nail studios, massage',
    icon: 'mdi:spa',
    color: '#EC4899',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('beauty'),
    defaultRoles: ['Stylist', 'Therapist', 'Receptionist'],
    requiredFeatures: ['appointments', 'services', 'staff'],
    optionalFeatures: ['treatments', 'packages', 'calendar', 'clients'],
  },
  fitness: {
    type: 'fitness',
    label: 'Fitness & Sports',
    description: 'Gyms, yoga studios, personal training',
    icon: 'mdi:dumbbell',
    color: '#F97316',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('fitness'),
    defaultRoles: ['Trainer', 'Instructor', 'Manager'],
    requiredFeatures: ['classes', 'memberships', 'equipment'],
    optionalFeatures: ['calendar', 'clients', 'analytics', 'reports'],
  },
  education: {
    type: 'education',
    label: 'Education & Training',
    description: 'Schools, tutoring centers, training institutes',
    icon: 'mdi:school',
    color: '#6366F1',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('education'),
    defaultRoles: ['Teacher', 'Instructor', 'Administrator'],
    requiredFeatures: ['classes', 'students', 'curriculum'],
    optionalFeatures: ['grades', 'reports', 'calendar', 'analytics'],
  },
  automotive: {
    type: 'automotive',
    label: 'Automotive Services',
    description: 'Car repairs, maintenance, car wash',
    icon: 'mdi:car',
    color: '#6B7280',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('automotive'),
    defaultRoles: ['Mechanic', 'Service Advisor', 'Manager'],
    requiredFeatures: ['appointments', 'services', 'vehicles'],
    optionalFeatures: ['maintenance', 'parts', 'invoicing', 'calendar'],
  },
  commerce: {
    type: 'commerce',
    label: 'Retail & Commerce',
    description: 'Shops, boutiques, retail services',
    icon: 'mdi:store',
    color: '#10B981',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('commerce'),
    defaultRoles: ['Sales Associate', 'Manager', 'Cashier'],
    requiredFeatures: ['products', 'inventory', 'sales'],
    optionalFeatures: ['customers', 'analytics', 'reports', 'pos'],
  },
  professional: {
    type: 'professional',
    label: 'Professional Services',
    description: 'Law firms, architecture, engineering',
    icon: 'mdi:tie',
    color: '#1F2937',
    category: 'new',
    defaultSettings: getDefaultSettingsForType('professional'),
    defaultRoles: ['Partner', 'Associate', 'Assistant'],
    requiredFeatures: ['appointments', 'clients', 'projects'],
    optionalFeatures: ['cases', 'billing', 'calendar', 'reports'],
  },
};
